import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Star,
  ArrowUpRight,
  RefreshCw,
  Edit,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { baseURL } from "@/config/APIConfig";
import EmailListItem from "./EmailListItem";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API = baseURL.dashboardApi;

const MailboxList = ({
  onEmailSelect,
  onCompose,
  onFolderChange,
  onAutomationDashboard,
}) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlags, setSelectedFlags] = useState({});
  const [activeFolder, setActiveFolder] = useState("inbox"); // 'inbox', 'sent', 'trash'
  const [filterFlag, setFilterFlag] = useState("all"); // 'all', 'Done', 'Exception', 'Important', 'Follow-up'

  useEffect(() => {
    fetchEmails();
    if (onFolderChange) {
      onFolderChange(activeFolder);
    }
  }, [activeFolder]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      let endpoint = `${API}/mailbox/inbox`;
      if (activeFolder === "sent") {
        endpoint = `${API}/mailbox/sent`;
      } else if (activeFolder === "trash") {
        endpoint = `${API}/mailbox/trash`;
      }

      const response = await axios.get(endpoint);
      setEmails(response.data.emails || []);

      // Fetch flags for each email
      const flagPromises = response.data.emails.map((email) =>
        axios
          .get(`${API}/mailbox/flags/${email.id}`)
          .then((res) => ({ id: email.id, flag: res.data.flag }))
          .catch(() => ({ id: email.id, flag: "" })),
      );

      const flags = await Promise.all(flagPromises);
      const flagsMap = {};
      flags.forEach((f) => {
        flagsMap[f.id] = f.flag;
      });
      setSelectedFlags(flagsMap);
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast.error("Failed to fetch emails");
    } finally {
      setLoading(false);
    }
  };

  const updateFlag = async (emailId, flag) => {
    try {
      await axios.patch(`${API}/mailbox/flag/${emailId}`, { flag });
      setSelectedFlags((prev) => ({ ...prev, [emailId]: flag }));
      toast.success(`Marked as ${flag}`);
    } catch (error) {
      console.error("Error updating flag:", error);
      toast.error("Failed to update flag");
    }
  };

  const deleteEmail = async (emailId, permanent = false) => {
    try {
      if (permanent) {
        await axios.delete(
          `${API}/mailbox/email/${emailId}?permanent=true&folder=${activeFolder}`,
        );
        toast.success("Email permanently deleted");
      } else {
        await axios.delete(
          `${API}/mailbox/email/${emailId}?folder=${activeFolder}`,
        );
        toast.success("Email moved to trash");
      }
      // Refresh the list
      fetchEmails();
    } catch (error) {
      console.error("Error deleting email:", error);
      toast.error("Failed to delete email");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const getFlagIcon = (flag) => {
    switch (flag) {
      case "Done":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "Exception":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "Important":
        return <Star className="w-4 h-4 text-yellow-500" />;
      case "Follow-up":
        return <ArrowUpRight className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getFlagColor = (flag) => {
    switch (flag) {
      case "Done":
        return "bg-green-50 border-green-200 text-green-700";
      case "Exception":
        return "bg-red-50 border-red-200 text-red-700";
      case "Important":
        return "bg-yellow-50 border-yellow-200 text-yellow-700";
      case "Follow-up":
        return "bg-blue-50 border-blue-200 text-blue-700";
      default:
        return "bg-slate-50 border-slate-200 text-slate-700";
    }
  };

  // Filter emails by flag
  const filteredEmails =
    filterFlag === "all"
      ? emails
      : emails.filter((email) => selectedFlags[email.id] === filterFlag);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0072FF]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Mailbox
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              {filteredEmails.length}{" "}
              {filteredEmails.length === 1 ? "message" : "messages"}
            </p>
          </div>
          <div className="flex space-x-3">
            {onAutomationDashboard && (
              <button
                onClick={onAutomationDashboard}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Automation Dashboard</span>
              </button>
            )}
            <button
              onClick={fetchEmails}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={onCompose}
              className="px-4 py-2 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Compose</span>
            </button>
          </div>
        </div>

        {/* Folder Tabs */}
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => setActiveFolder("inbox")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeFolder === "inbox"
                ? "bg-[#0072FF] text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            Inbox
          </button>
          <button
            onClick={() => setActiveFolder("sent")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeFolder === "sent"
                ? "bg-[#0072FF] text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            Sent
          </button>
          <button
            onClick={() => setActiveFolder("trash")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeFolder === "trash"
                ? "bg-[#0072FF] text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            Trash
          </button>
        </div>

        {/* Filter Options */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Filter:
          </span>
          <button
            onClick={() => setFilterFlag("all")}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              filterFlag === "all"
                ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterFlag("Done")}
            className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center space-x-1 ${
              filterFlag === "Done"
                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-green-50 dark:hover:bg-green-900/20"
            }`}
          >
            <CheckCircle className="w-3 h-3" />
            <span>Done</span>
          </button>
          <button
            onClick={() => setFilterFlag("Exception")}
            className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center space-x-1 ${
              filterFlag === "Exception"
                ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            }`}
          >
            <AlertCircle className="w-3 h-3" />
            <span>Exception</span>
          </button>
          <button
            onClick={() => setFilterFlag("Important")}
            className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center space-x-1 ${
              filterFlag === "Important"
                ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
            }`}
          >
            <Star className="w-3 h-3" />
            <span>Important</span>
          </button>
          <button
            onClick={() => setFilterFlag("Follow-up")}
            className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center space-x-1 ${
              filterFlag === "Follow-up"
                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            }`}
          >
            <ArrowUpRight className="w-3 h-3" />
            <span>Follow-up</span>
          </button>
        </div>
      </div>

      {/* Email List */}
      <div className="divide-y divide-slate-200 dark:divide-slate-800">
        {filteredEmails.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-16 h-16 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">
              {filterFlag === "all"
                ? "No emails found"
                : `No emails with "${filterFlag}" flag`}
            </p>
          </div>
        ) : (
          filteredEmails.map((email) => (
            <EmailListItem
              key={email.id}
              email={email}
              selectedFlags={selectedFlags}
              activeFolder={activeFolder}
              onEmailSelect={onEmailSelect}
              updateFlag={updateFlag}
              deleteEmail={deleteEmail}
              formatDate={formatDate}
              getFlagColor={getFlagColor}
              getFlagIcon={getFlagIcon}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MailboxList;
